// YouTube helpers. The hero video field takes an ordinary YouTube link — the
// kind you copy from the address bar or the Share button — and we turn it into
// an embed here rather than asking editors to paste embed markup.

const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
  'youtu.be',
  'www.youtu.be',
]);

// A video id is always 11 characters of the URL-safe base64 alphabet.
const VIDEO_ID = /^[\w-]{11}$/;

/**
 * Pulls the video id out of a YouTube link, or returns null if it isn't one.
 * Accepts watch, youtu.be, embed, shorts and live forms, with or without a
 * scheme, and ignores extra query params (playlist position, `si`, `t`, …).
 */
export function parseYouTubeVideoId(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  let url: URL;
  try {
    // Editors paste "youtube.com/watch?v=…" as often as the full link.
    url = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
  } catch {
    return null;
  }

  if (!YOUTUBE_HOSTS.has(url.hostname.toLowerCase())) return null;

  const segments = url.pathname.split('/').filter(Boolean);

  // youtu.be/<id>
  if (url.hostname.toLowerCase().endsWith('youtu.be')) {
    return segments[0] && VIDEO_ID.test(segments[0]) ? segments[0] : null;
  }

  // youtube.com/watch?v=<id>
  const v = url.searchParams.get('v');
  if (v && VIDEO_ID.test(v)) return v;

  // youtube.com/{embed,shorts,live,v}/<id>
  if (segments.length >= 2 && ['embed', 'shorts', 'live', 'v'].includes(segments[0])) {
    return VIDEO_ID.test(segments[1]) ? segments[1] : null;
  }

  return null;
}

/**
 * Embed URL for a video playing as a silent, looping background: no controls,
 * no keyboard, no related videos at the end. `loop` only repeats a playlist, so
 * the video is handed to itself as a one-item one — that is YouTube's own
 * documented way of looping a single video.
 */
export function youTubeBackgroundEmbedUrl(videoId: string): string {
  const params = new URLSearchParams({
    autoplay: '1',
    mute: '1',
    loop: '1',
    playlist: videoId,
    controls: '0',
    playsinline: '1',
    rel: '0',
    modestbranding: '1',
    disablekb: '1',
    fs: '0',
    iv_load_policy: '3',
    // Lets the player report when it actually starts playing, so a poster can
    // cover the title card and play button YouTube shows while it loads.
    enablejsapi: '1',
  });
  if (typeof window !== 'undefined') params.set('origin', window.location.origin);
  // nocookie host so a visitor who never plays the video isn't tracked by it.
  return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`;
}
